// Unified filter-state model for the Orders page (Phase 4a + 4b).
//
// Every filter surface — chip row, Add-filter menu, summary strip, the table
// query — reads from and writes to a single OrderFilterState.
//
// Composition rule (locked decision, see phase-4-brief.md):
//   - Within a multi-select axis, chips OR.
//   - Across axes, chips AND.
//   - An axis with no selections is a no-op (matches every order).
//   - Order Window (`date`) is single-select — at most one chip at a time.

export type OrderStatus = "pending" | "reviewed" | "cancelled";
export type FulfillmentTypeFilter = "delivery" | "pickup";
export type QbSyncFilter = "sent" | "not-sent";
export type OrderSourceFilter = "customer_app" | "kiosk" | "admin_manual" | "sms_agent";

// Order Window axis — single-select. The picker is a list of named windows
// (the same options the legacy date dropdown showed). Two forms plus null:
//   - { kind: "specific"; value }  — one fulfillment day (YYYY-MM-DD)
//   - { kind: "unscheduled" }       — orders with no fulfillment date set
// FreshOrder organizes orders by these discrete windows, not by arbitrary
// date ranges (procurement, pick sheets, the whole workflow key off
// fulfillment date). See phase-4-brief.md → "The Order Window axis".
export type DateFilter =
  | null
  | { kind: "specific"; value: string }
  | { kind: "unscheduled" };

export interface OrderFilterState {
  statuses: OrderStatus[];
  fulfillmentTypes: FulfillmentTypeFilter[];
  quickbooks: QbSyncFilter[];
  sources: OrderSourceFilter[];
  date: DateFilter;
}

export const EMPTY_FILTER_STATE: OrderFilterState = {
  statuses: [],
  fulfillmentTypes: [],
  quickbooks: [],
  sources: [],
  date: null,
};

// Minimum shape an order needs to expose to be filterable. Declaring this
// locally (rather than importing OrderWithDetails) keeps the filter logic
// independent of the Drizzle row type and easier to unit-test.
export interface OrderForFilter {
  status: string;
  fulfillmentDate?: string | null;
  pickupDate?: string | null;
  fulfillmentType?: string | null;
  quickbooksSyncedAt?: string | Date | null;
  source?: string | null;
}

// Resolve an order's effective fulfillment type, preserving the legacy
// behavior in seller-dashboard.tsx: orders pre-dating the fulfillmentType
// column have null fulfillmentType + a pickupDate, and are treated as pickup.
function resolveFulfillmentType(order: OrderForFilter): FulfillmentTypeFilter | null {
  const raw = order.fulfillmentType ?? null;
  if (raw === "delivery") return "delivery";
  if (raw === "pickup") return "pickup";
  if (!raw && order.pickupDate) return "pickup";
  return null;
}

// Effective fulfillment date for an order: fulfillmentDate if set, else the
// legacy pickupDate, else null (= unscheduled).
function resolveFulfillmentDate(order: OrderForFilter): string | null {
  return order.fulfillmentDate ?? order.pickupDate ?? null;
}

export function matchesFilters(order: OrderForFilter, state: OrderFilterState): boolean {
  // Status axis — OR within
  if (state.statuses.length > 0 && !state.statuses.includes(order.status as OrderStatus)) {
    return false;
  }

  // Fulfillment-type axis — OR within
  if (state.fulfillmentTypes.length > 0) {
    const effective = resolveFulfillmentType(order);
    if (!effective || !state.fulfillmentTypes.includes(effective)) return false;
  }

  // QuickBooks-sync axis — OR within. `sent` and `not-sent` are mutually
  // exclusive states; if both chips are present the OR collapses to "any"
  // (equivalent to no chip), which is the correct fallthrough.
  if (state.quickbooks.length > 0) {
    const isSent = order.quickbooksSyncedAt != null;
    const wantsSent = state.quickbooks.includes("sent");
    const wantsNotSent = state.quickbooks.includes("not-sent");
    if (!((wantsSent && isSent) || (wantsNotSent && !isSent))) return false;
  }

  // Order Source axis — OR within. Missing source on the row defaults to
  // admin_manual (matches backend default + backfill behavior).
  if (state.sources.length > 0) {
    const effective: OrderSourceFilter =
      order.source === "customer_app" || order.source === "kiosk" || order.source === "admin_manual" || order.source === "sms_agent"
        ? order.source
        : "admin_manual";
    if (!state.sources.includes(effective)) return false;
  }

  // Order Window axis — single-select chip.
  if (state.date) {
    const fDate = resolveFulfillmentDate(order);
    if (state.date.kind === "specific") {
      if (fDate !== state.date.value) return false;
    } else {
      // "unscheduled" — match orders with no fulfillment date
      if (fDate) return false;
    }
  }

  return true;
}

export function isFilterStateEmpty(state: OrderFilterState): boolean {
  return (
    state.statuses.length === 0 &&
    state.fulfillmentTypes.length === 0 &&
    state.quickbooks.length === 0 &&
    state.sources.length === 0 &&
    state.date === null
  );
}

// Server-side fulfillmentDate query param. The /api/orders endpoint accepts
// a single fulfillmentDate value (specific YYYY-MM-DD or the string
// "unscheduled"); the other axes filter client-side from the same 200-row
// response. Used by the orders query so the wire payload narrows when a
// date chip is active.
//
// Bounded-results note (4a requirement): the server query is capped at
// limit=200 and the UI paginates the result at 10/25/50/100 rows per page
// (see seller-dashboard.tsx — VALID_PAGE_SIZES + paginatedOrders). The DOM
// therefore never renders more than 100 rows at a time even when every
// filter is cleared. The 200-row server cap means a truly unfiltered list
// is also truncated, not just paginated — older synced orders past row 200
// require a date chip to surface; this is acceptable for v1 because the
// page-load default already pins to a single fulfillment day.
export function filterStateToFulfillmentDateParam(state: OrderFilterState): string | null {
  if (!state.date) return null;
  if (state.date.kind === "unscheduled") return "unscheduled";
  return state.date.value;
}

// Server-side `source` query param: the Order Source chips, comma-joined (OR
// within the axis), so "text orders" narrows on the wire like a date chip does.
// Null when the axis has no chip.
export function filterStateToSourceParam(state: OrderFilterState): string | null {
  if (state.sources.length === 0) return null;
  return [...state.sources].sort().join(",");
}
